import React, { useMemo, useState } from "react";
import { CalendarDays, Clock3, Route, Search } from "lucide-react";

export default function HistoryFiltersSidebar({
  devices = [],
  selectedDeviceId = "",
  onSelectDevice,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  fromTime,
  toTime,
  onFromTimeChange,
  onToTimeChange,
  stopsFilter,
  onStopsFilterChange,
  onSearch,
  loading = false,
  summaryItems = [],
  quickRanges = [],
  trips = [],
  selectedTripId = "",
  onSelectTrip,
  error = "",
}) {
  const [vehicleQuery, setVehicleQuery] = useState("");

  const filteredDevices = useMemo(() => {
    const query = String(vehicleQuery || "").trim().toLowerCase();
    if (!query) return devices;
    return devices.filter((device) => {
      const label = String(device?.name || device?.vehicleNumber || device?.imei || "Vehicle").toLowerCase();
      return label.includes(query);
    });
  }, [devices, vehicleQuery]);

  return (
    <aside className="history-left-column">
      <section className="history-filters-card">
        <div className="history-card-head">
          <div>
            <h2>History Filters</h2>
            <p>Choose vehicle and time range</p>
          </div>
        </div>

        <div className="history-field-grid">
          <label className="history-field">
            <span>Vehicle</span>
            <div className="history-input-icon history-input-icon-search">
              <Search size={16} />
              <input
                type="text"
                value={vehicleQuery}
                onChange={(event) => setVehicleQuery(event.target.value)}
                placeholder="Search vehicle"
              />
            </div>
            <select value={selectedDeviceId} onChange={(event) => onSelectDevice?.(event.target.value)}>
              <option value="">Select vehicle</option>
              {filteredDevices.map((device) => (
                <option key={device?._id} value={device?._id}>
                  {device?.name || device?.vehicleNumber || device?.imei || "Vehicle"}
                </option>
              ))}
            </select>
          </label>

          <label className="history-field">
            <span>Date</span>
            <div className="history-input-icon">
              <CalendarDays size={16} />
              <input type="date" value={startDate} onChange={(event) => onStartDateChange?.(event.target.value)} />
            </div>
          </label>

          <div className="history-field-row">
            <label className="history-field">
              <span>From</span>
              <div className="history-input-icon">
                <Clock3 size={16} />
                <input type="time" value={fromTime} onChange={(event) => onFromTimeChange?.(event.target.value)} />
              </div>
            </label>
            <label className="history-field">
              <span>To</span>
              <div className="history-input-icon">
                <Clock3 size={16} />
                <input type="time" value={toTime} onChange={(event) => onToTimeChange?.(event.target.value)} />
              </div>
            </label>
          </div>

          <label className="history-field">
            <span>Stops</span>
            <select value={stopsFilter} onChange={(event) => onStopsFilterChange?.(event.target.value)}>
              <option value="all">All Stops</option>
              <option value="with_stops">With Stops</option>
              <option value="without_stops">Without Stops</option>
            </select>
          </label>

          <label className="history-field">
            <span>End Date</span>
            <div className="history-input-icon">
              <CalendarDays size={16} />
              <input type="date" value={endDate} onChange={(event) => onEndDateChange?.(event.target.value)} />
            </div>
          </label>

          <button
            type="button"
            className="history-primary-button"
            onClick={onSearch}
            disabled={!selectedDeviceId || loading}
          >
            <Search size={16} />
            {loading ? "Loading..." : "Show History"}
          </button>
        </div>

        <div className="history-quick-select">
          <div className="history-section-label">Quick Select</div>
          <div className="history-quick-range-row">
            {quickRanges.map((item) => (
              <button key={item.label} type="button" className="history-chip-button" onClick={item.onClick}>
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="history-summary-card history-panel-card">
        <div className="history-card-head compact">
          <h3>Trip Summary</h3>
        </div>
        <div className="history-summary-grid">
          {summaryItems.map(([label, value]) => (
            <div key={label} className="history-summary-item">
              <span>{label}</span>
              <strong>{value}</strong>
            </div>
          ))}
        </div>
      </section>

      <section className="history-routes-card history-panel-card">
        <div className="history-card-head compact">
          <div className="history-routes-title">
            <Route size={15} />
            <h3>Routes</h3>
          </div>
        </div>
        {error ? <div className="history-inline-error">{error}</div> : null}
        {trips.length === 0 && !error ? <div className="history-empty-note">No trips found for selected range.</div> : null}
        <div className="history-route-list">
          {trips.map((trip) => (
            <button
              key={trip.id}
              type="button"
              className={`history-trip-item ${String(selectedTripId) === String(trip.id) ? "active" : ""}`}
              onClick={() => onSelectTrip?.(trip)}
            >
              <div className="history-trip-item-date">{trip.date}</div>
              <div className="history-trip-item-meta">
                <span>{Number(trip.distance || 0).toFixed(1)} km</span>
                <span>{trip.durationLabel}</span>
                <span>{Math.round(Number(trip.maxSpeed || 0))} km/h</span>
              </div>
            </button>
          ))}
        </div>
      </section>
    </aside>
  );
}
